import React from 'react';
import { MdComputer, MdCheck } from 'react-icons/md';
import { Button, Modal } from '@aibuild/ui';
import type { ProcessNode, TaskNodeData } from './graphTypes';
import { useOrgMembers } from '../hooks/useOrgMembers';
import { useGroups } from '../hooks/useGroups';
import {
  Body,
  Explanation,
  NodeContext,
  NodeLabel,
  RoleHint,
} from './RunnerClaimDialog.styled';

export interface RunnerClaimDialogProps {
  /** When non-null the dialog is open and points at this waiting task node. */
  runnerWait: { readonly nodeId: string; readonly iteration: number } | null;
  /** Live editor nodes — looked up to render the task's label + assignees. */
  nodes: readonly ProcessNode[];
  /** True while the parent's accept mutation is in flight. */
  isAccepting: boolean;
  /** Records consent + resumes the run. Throws on error so the dialog stays open. */
  onAccept: () => Promise<void>;
  /** Dismisses the dialog without consenting. */
  onClose: () => void;
  dataTestId?: string;
}

function renderAssigneeHint(
  data: TaskNodeData | undefined,
  assigneeNames: readonly string[] | null,
  groupName: string | null,
  dataTestId: string,
): React.ReactElement {
  if (data?.assigneeType === 'GROUP') {
    return (
      <RoleHint data-testid={`${dataTestId}__hint-group`}>
        {groupName
          ? `Assigned to group: ${groupName}`
          : 'Assigned to a group.'}
      </RoleHint>
    );
  }
  if (assigneeNames && assigneeNames.length > 0) {
    return (
      <RoleHint data-testid={`${dataTestId}__hint-users`}>
        Assigned to: {assigneeNames.join(', ')}
      </RoleHint>
    );
  }
  return (
    <RoleHint data-testid={`${dataTestId}__hint-generic`}>
      Only an assigned user can allow this run on their machine.
    </RoleHint>
  );
}

/**
 * Modal asking an assignee to allow a process run onto their local runner.
 *
 * <p>A local task node parks in {@code waiting_for_runner} until an assignee
 * consents; the backend then claims their online runner. Consent is enforced
 * server-side — this dialog surfaces to any run watcher and a non-assignee's
 * accept is rejected with a 403.</p>
 */
const RunnerClaimDialog: React.FC<RunnerClaimDialogProps> = ({
  runnerWait,
  nodes,
  isAccepting,
  onAccept,
  onClose,
  dataTestId = 'runner-claim-dialog',
}) => {
  const node = runnerWait ? nodes.find((n) => n.id === runnerWait.nodeId) : undefined;
  const data = node?.data as TaskNodeData | undefined;
  const { data: orgMembers } = useOrgMembers();
  const { data: orgGroups } = useGroups();

  const isGroupAssignee = data?.assigneeType === 'GROUP';
  const groupId = data?.assigneeGroupId;
  const groupName =
    isGroupAssignee && groupId && orgGroups
      ? orgGroups.find((g) => g.id === groupId)?.name ?? null
      : null;

  const assigneeIdSet =
    data?.assigneeIds && data.assigneeIds.length > 0
      ? new Set(data.assigneeIds)
      : null;
  const assigneeNames =
    assigneeIdSet && orgMembers
      ? orgMembers
          .filter((m) => assigneeIdSet.has(m.id))
          .map((m) => m.displayName || m.email)
      : null;

  const handleAccept = async () => {
    try {
      await onAccept();
    } catch {
      // Parent surfaces the error via snackbar; keep the dialog open to retry.
    }
  };

  return (
    <Modal
      open={Boolean(runnerWait)}
      onClose={isAccepting ? () => undefined : onClose}
      title="Run on your machine"
      subtitle="A process step is waiting for your local runner."
      icon={<MdComputer />}
      size="md"
      dataTestId={dataTestId}
      hideCloseButton={isAccepting}
      disableEscape={isAccepting}
      disableBackdropClose={isAccepting}
      footer={
        <>
          <Button
            variant="text"
            onClick={onClose}
            disabled={isAccepting}
            dataTestId={`${dataTestId}__cancel`}
          >
            Not now
          </Button>
          <Button
            variant="filled"
            onClick={() => void handleAccept()}
            disabled={isAccepting}
            leadingIcon={<MdCheck />}
            dataTestId={`${dataTestId}__accept`}
          >
            {isAccepting ? 'Allowing…' : 'Allow on my machine'}
          </Button>
        </>
      }
    >
      <Body data-testid={`${dataTestId}__body`}>
        <NodeContext>
          <NodeLabel data-testid={`${dataTestId}__node-label`}>
            {data?.label ?? 'Task'}
          </NodeLabel>
          {renderAssigneeHint(data, assigneeNames, groupName, dataTestId)}
        </NodeContext>
        <Explanation data-testid={`${dataTestId}__explanation`}>
          This step needs one of your connected local runners to run it. If you
          allow it, the run continues on your machine and you may see it drive
          local apps while it works. It stays paused until an assignee allows it
          or the wait times out.
        </Explanation>
      </Body>
    </Modal>
  );
};

export default RunnerClaimDialog;
